import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type Kpi = { value: string; prev?: string; deltaPercent?: number | null };

export type OwnerSummary = {
  period: { from: string; to: string };
  kpis: {
    contractedRevenue: Kpi;
    collected: Kpi;
    expenses: Kpi;
    unitsSold: Kpi;
    cashPosition: Kpi;
    outstanding: Kpi;
    overdue90plus: Kpi;
  };
};

export type RevenueTrend = { buckets: Array<{ month: string; contracted: string; collected: string }> };
export type CashFlowStatement = {
  period: { from: string; to: string };
  opening: string;
  income: Array<{ category: string; amount: string }>;
  expense: Array<{ category: string; amount: string }>;
  totalIncome: string;
  totalExpense: string;
  net: string;
  closing: string;
};
export type SalesReport = {
  period: { from: string; to: string };
  groupBy: 'month' | 'project' | 'layout';
  rows: Array<{ key: string; label: string; units: number; total: string; discount: string; avgPricePerSqm: string }>;
  totals: { units: number; total: string; discount: string };
};
export type CollectionsReport = { buckets: Array<{ month: string; expected: string; collected: string; ratePercent: number }> };
export type InventoryReport = {
  total: number;
  absorptionPercent: number;
  remainingStockValue: string;
  byStatus: Array<{ status: string; count: number; value: string }>;
  byLayout: Array<{ layout: string; count: number; value: string }>;
};

type Period = { from?: string; to?: string; projectId?: string };

export function useOwnerSummary(p: Period) {
  return useQuery({
    queryKey: ['owner-summary', p],
    queryFn: async () =>
      (await api.get<{ data: OwnerSummary }>('/reports/owner/summary', { params: p })).data.data,
  });
}

export function useRevenueTrend(months = 12, projectId?: string) {
  return useQuery({
    queryKey: ['owner-revenue-trend', months, projectId],
    queryFn: async () =>
      (await api.get<{ data: RevenueTrend }>('/reports/owner/revenue-trend', { params: { months, projectId } })).data.data,
  });
}

export function useCashFlowStatement(p: { from?: string; to?: string; accountId?: string }) {
  return useQuery({
    queryKey: ['owner-cashflow', p],
    queryFn: async () =>
      (await api.get<{ data: CashFlowStatement }>('/reports/owner/cash-flow', { params: p })).data.data,
  });
}

export function useSalesReport(p: { from?: string; to?: string; groupBy?: 'month' | 'project' | 'layout' }) {
  return useQuery({
    queryKey: ['owner-sales', p],
    queryFn: async () =>
      (await api.get<{ data: SalesReport }>('/reports/owner/sales', { params: p })).data.data,
  });
}

export function useCollectionsReport(months = 12, projectId?: string) {
  return useQuery({
    queryKey: ['owner-collections', months, projectId],
    queryFn: async () =>
      (await api.get<{ data: CollectionsReport }>('/reports/owner/collections', { params: { months, projectId } })).data.data,
  });
}

export function useInventoryReport(projectId?: string) {
  return useQuery({
    queryKey: ['owner-inventory', projectId],
    queryFn: async () =>
      (await api.get<{ data: InventoryReport }>('/reports/owner/inventory', { params: { projectId } })).data.data,
  });
}
